import {
  autoUpdate,
  computePosition,
  flip,
  offset,
  shift,
  type VirtualElement
} from '@floating-ui/dom'
import { ReactRenderer } from '@tiptap/react'
import type { SuggestionOptions, SuggestionProps } from '@tiptap/suggestion'
import type { ForwardRefExoticComponent, RefAttributes } from 'react'

export type SuggestionListRef = {
  onKeyDown: (props: { event: KeyboardEvent }) => boolean | undefined
}

const getVirtualElement = <Item>(
  props: SuggestionProps<Item>
): VirtualElement => ({
  getBoundingClientRect: () => props.clientRect?.() ?? new DOMRect()
})

const positionPopup = (popup: HTMLDivElement, reference: VirtualElement) =>
  computePosition(reference, popup, {
    placement: 'bottom-start',
    middleware: [offset(5), flip(), shift()]
  }).then(({ x, y }) => {
    Object.assign(popup.style, { left: `${x}px`, top: `${y}px` })
  })

export const createSuggestionRenderer =
  <Item, Props extends Record<string, unknown>>(
    ListComponent: ForwardRefExoticComponent<
      Props & RefAttributes<SuggestionListRef>
    >
  ): NonNullable<SuggestionOptions<Item>['render']> =>
  () => {
    let component: ReactRenderer<
      SuggestionListRef,
      Props & RefAttributes<SuggestionListRef>
    > | null = null
    let popup: HTMLDivElement | null = null
    let cleanup: (() => void) | null = null

    return {
      onStart: (props) => {
        const renderer = new ReactRenderer(ListComponent, {
          props,
          editor: props.editor
        })
        component = renderer

        popup = document.createElement('div')
        popup.style.position = 'absolute'
        popup.style.zIndex = '1000'
        popup.appendChild(renderer.element)
        document.body.appendChild(popup)

        const reference = getVirtualElement(props)
        const updatePosition = () => {
          if (!popup || !props.clientRect) return
          positionPopup(popup, reference)
        }

        updatePosition()
        cleanup = autoUpdate(reference, popup, updatePosition)
      },

      onUpdate: (props) => {
        component?.updateProps(props)

        if (!props.clientRect || !popup) {
          return
        }

        positionPopup(popup, getVirtualElement(props))
      },

      onKeyDown: (props) => {
        if (props.event.key === 'Escape') {
          if (popup) {
            popup.style.display = 'none'
          }
          cleanup?.()
          cleanup = null
          component?.destroy()

          return true
        }

        return component?.ref?.onKeyDown(props) ?? false
      },

      onExit: () => {
        cleanup?.()
        cleanup = null
        popup?.remove()
        popup = null
        component?.destroy()
      }
    }
  }
